import { FC, useState } from 'react';
import {
    Box,
    Chip,
    CircularProgress,
    Alert,
    Grid,
    LinearProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { StatsDateFilter } from '../common/StatsDateFilter';
import { KpiCard } from '../common/KpiCard';
import { StatsBarChart } from '../common/StatsBarChart';
import { StatsLineChart } from '../common/StatsLineChart';
import { useRevenue } from '../../../hooks/analytics/useRevenue';

function statusChipColor(status: string): 'success' | 'info' | 'error' | 'default' {
    const s = status.toUpperCase();
    if (s === 'NEW' || s === '신규') return 'success';
    if (s === 'RENEWED' || s === '갱신') return 'info';
    if (s === 'CANCELLED' || s === '해지') return 'error';
    return 'default';
}

const RevenuePage: FC = () => {
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    const handleDateChange = (start: string, end: string) => {
        setStartDate(start);
        setEndDate(end);
    };

    const { data, isLoading, isError, error } = useRevenue(startDate, endDate);

    const subKpi = data?.subscriptionKpi;
    const revKpi = data?.revenueKpi;
    const unitEcon = data?.unitEconomics;
    const revenueTrend = data?.revenueTrend ?? [];
    const arpuTrend = data?.arpuTrend ?? [];
    const recentChanges = data?.recentChanges ?? [];
    const planDistribution = data?.planDistribution ?? [];

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h5" sx={{ mb: 2 }}>
                매출/구독 통계
            </Typography>

            {/* Date Filter */}
            <Box sx={{ mb: 3 }}>
                <StatsDateFilter onChange={handleDateChange} />
            </Box>

            {isLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                </Box>
            )}

            {isError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {(error as Error)?.message ?? '데이터를 불러오지 못했습니다.'}
                </Alert>
            )}

            {data && (
                <>
                    {/* 구독 현황 */}
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                        구독 현황
                    </Typography>
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <KpiCard
                                title="활성 구독자"
                                value={(subKpi?.activeSubscribers ?? 0).toLocaleString()}
                                changePercent={subKpi?.activeChange}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <KpiCard
                                title="구독 전환율"
                                value={`${(subKpi?.conversionRate ?? 0).toFixed(1)}%`}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <KpiCard
                                title="신규 구독"
                                value={(subKpi?.newSubscriptions ?? 0).toLocaleString()}
                                changePercent={subKpi?.newChange}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <KpiCard
                                title="구독 해지"
                                value={(subKpi?.churned ?? 0).toLocaleString()}
                                subtitle={`이탈률 ${(subKpi?.churnRate ?? 0).toFixed(1)}%`}
                            />
                        </Grid>
                    </Grid>

                    {/* 매출 */}
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                        매출
                    </Typography>
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <KpiCard
                                title="구독 매출"
                                value={`\u20A9${(revKpi?.subscriptionRevenue ?? 0).toLocaleString()}`}
                                changePercent={revKpi?.subscriptionChange}
                                variant="gradient"
                                color="blue"
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <KpiCard
                                title="포인트 매출"
                                value={`\u20A9${(revKpi?.pointRevenue ?? 0).toLocaleString()}`}
                                changePercent={revKpi?.pointChange}
                                variant="gradient"
                                color="green"
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <KpiCard
                                title="총 매출"
                                value={`\u20A9${(revKpi?.totalRevenue ?? 0).toLocaleString()}`}
                                changePercent={revKpi?.totalChange}
                                variant="gradient"
                                color="orange"
                            />
                        </Grid>
                    </Grid>

                    {/* 유닛 이코노믹스 */}
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                        유닛 이코노믹스
                    </Typography>
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <KpiCard
                                title="ARPU"
                                value={`\u20A9${(unitEcon?.arpu ?? 0).toLocaleString()}`}
                                subtitle="총 매출 / 활성 사용자"
                                changePercent={unitEcon?.arpuChange}
                                variant="border"
                                color="blue"
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <KpiCard
                                title="ARPPU"
                                value={`\u20A9${(unitEcon?.arppu ?? 0).toLocaleString()}`}
                                subtitle="총 매출 / 결제 사용자"
                                changePercent={unitEcon?.arppuChange}
                                variant="border"
                                color="purple"
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <KpiCard
                                title="구독 ARPPU"
                                value={`\u20A9${(unitEcon?.subscriptionArppu ?? 0).toLocaleString()}`}
                                subtitle="구독 매출 / 구독자"
                                changePercent={unitEcon?.subscriptionArppuChange}
                                variant="border"
                                color="blue"
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <KpiCard
                                title="결제 전환율 (PUR)"
                                value={`${(unitEcon?.pur ?? 0).toFixed(1)}%`}
                                subtitle="결제 사용자 / 활성 사용자"
                                changePercent={unitEcon?.purChange}
                                variant="border"
                                color="orange"
                            />
                        </Grid>
                    </Grid>

                    {/* Charts */}
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid size={{ xs: 12, md: 8 }}>
                            <Paper sx={{ p: 2 }}>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    일별 매출 추이
                                </Typography>
                                <StatsBarChart
                                    data={revenueTrend as unknown as Record<string, unknown>[]}
                                    series={[
                                        { dataKey: 'subscriptionRevenue', name: '구독', color: '#1976d2', stackId: 'revenue' },
                                        { dataKey: 'pointRevenue', name: '포인트', color: '#43a047', stackId: 'revenue' },
                                    ]}
                                    xAxisKey="date"
                                />
                            </Paper>
                        </Grid>
                        <Grid size={{ xs: 12, md: 4 }}>
                            <Paper sx={{ p: 2 }}>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    ARPU / ARPPU 추이
                                </Typography>
                                <StatsLineChart
                                    data={arpuTrend as unknown as Record<string, unknown>[]}
                                    series={[
                                        { dataKey: 'arppu', name: 'ARPPU', color: '#7b1fa2' },
                                        { dataKey: 'arpu', name: 'ARPU', color: '#1976d2' },
                                    ]}
                                    xAxisKey="date"
                                />
                            </Paper>
                        </Grid>
                    </Grid>

                    {/* Tables */}
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Paper sx={{ p: 2 }}>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    최근 구독 변동
                                </Typography>
                                <TableContainer sx={{ maxHeight: 360 }}>
                                    <Table size="small" stickyHeader>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>날짜</TableCell>
                                                <TableCell>사용자</TableCell>
                                                <TableCell>플랜</TableCell>
                                                <TableCell>상태</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {recentChanges.map((row, idx) => (
                                                <TableRow key={`${row.userId}-${row.date}-${idx}`}>
                                                    <TableCell>{row.date}</TableCell>
                                                    <TableCell>{row.nickname ?? `#${row.userId}`}</TableCell>
                                                    <TableCell>{row.plan}</TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            label={row.status}
                                                            size="small"
                                                            color={statusChipColor(row.status)}
                                                            variant="outlined"
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                            {recentChanges.length === 0 && (
                                                <TableRow>
                                                    <TableCell colSpan={4} align="center">
                                                        데이터 없음
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Paper>
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Paper sx={{ p: 2 }}>
                                <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
                                    구독 플랜 분포
                                </Typography>
                                {planDistribution.map((item) => (
                                    <Box key={item.label} sx={{ mb: 2 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                            <Typography variant="body2">{item.label}</Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {item.count.toLocaleString()} ({item.ratio.toFixed(1)}%)
                                            </Typography>
                                        </Box>
                                        <LinearProgress
                                            variant="determinate"
                                            value={item.ratio}
                                            sx={{ height: 8, borderRadius: 4 }}
                                        />
                                    </Box>
                                ))}
                                {planDistribution.length === 0 && (
                                    <Typography variant="body2" color="text.secondary" align="center">
                                        데이터 없음
                                    </Typography>
                                )}
                            </Paper>
                        </Grid>
                    </Grid>
                </>
            )}
        </Box>
    );
};

export default RevenuePage;
